import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  FileText, DollarSign, TrendingUp, Clock,
  LogOut, Home, User, Plus, UserPlus, Send
} from 'lucide-react';
import { toast } from 'sonner';

const INSURANCE_TYPES = [
  { value: 'auto', label: 'Automobile' },
  { value: 'habitation', label: 'Habitation' },
  { value: 'sante', label: 'Santé' },
  { value: 'prevoyance', label: 'Prévoyance' },
  { value: 'rc_pro', label: 'RC Pro' },
  { value: 'multirisque_pro', label: 'Multirisque Pro' },
  { value: 'decennale', label: 'Décennale' },
  { value: 'flotte', label: 'Flotte Auto' },
  { value: 'autre', label: 'Autre' }
];

export const ApporteurDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, isApporteur, isAdmin, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [contracts, setContracts] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadFormData, setLeadFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    insurance_type: '',
    details: ''
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isApporteur && !isAdmin) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isApporteur, isAdmin, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, contractsRes, commissionsRes, paymentsRes, leadsRes] = await Promise.all([
        axios.get(`${API_URL}/api/apporteur/dashboard`),
        axios.get(`${API_URL}/api/apporteur/contracts`),
        axios.get(`${API_URL}/api/apporteur/commissions`),
        axios.get(`${API_URL}/api/apporteur/payments`),
        axios.get(`${API_URL}/api/apporteur/leads`)
      ]);
      setStats(statsRes.data);
      setContracts(contractsRes.data);
      setCommissions(commissionsRes.data);
      setPayments(paymentsRes.data);
      setLeads(leadsRes.data);
    } catch (err) {
      console.error('Error fetching data', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitLead = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/apporteur/leads`, leadFormData);
      toast.success('Lead soumis avec succès !');
      setShowLeadForm(false);
      setLeadFormData({
        client_name: '',
        client_email: '',
        client_phone: '',
        insurance_type: '',
        details: ''
      });
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la soumission');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen pt-28 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const INSURANCE_LABELS = {
    'auto': 'Automobile',
    'habitation': 'Habitation',
    'sante': 'Santé',
    'prevoyance': 'Prévoyance',
    'rc_pro': 'RC Pro',
    'multirisque_pro': 'Multirisque Pro',
    'decennale': 'Décennale',
    'flotte': 'Flotte Auto',
    'autre': 'Autre'
  };

  return (
    <>
      <Helmet>
        <title>Mon Espace Apporteur | CAMBC Conseils</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-slate-100">
        {/* Top Navigation */}
        <div className="bg-blue-900 text-white py-4 px-6 fixed top-0 left-0 right-0 z-50">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-blue-300 hover:text-white">
                <Home size={20} />
              </Link>
              <span className="text-blue-500">|</span>
              <h1 className="text-xl font-bold">Mon Espace Apporteur</h1>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-blue-200">
                <User size={18} />
                <span>{user?.name}</span>
              </div>
              <Button 
                onClick={handleLogout}
                variant="outline" 
                className="border-blue-600 text-blue-200 hover:bg-blue-800"
              >
                <LogOut size={16} className="mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>

        <div className="pt-24 pb-12 px-6">
          <div className="max-w-7xl mx-auto">
            {/* Welcome Banner */}
            <Card className="bg-gradient-to-r from-blue-600 to-blue-800 text-white mb-8 border-0">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-2">Bienvenue, {user?.name} !</h2>
                <p className="text-blue-100">
                  Suivez vos contrats et commissions en temps réel.
                </p>
              </CardContent>
            </Card>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="bg-white shadow-lg border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Mes Contrats</p>
                      <p className="text-3xl font-bold text-slate-900">{stats?.total_contracts || 0}</p>
                      <p className="text-xs text-green-600 mt-1">{stats?.active_contracts || 0} actifs</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileText className="text-blue-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-lg border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Total Commissions</p>
                      <p className="text-3xl font-bold text-slate-900">
                        {(stats?.total_commissions_earned || 0).toLocaleString('fr-FR')} €
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="text-green-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-lg border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">En Attente</p>
                      <p className="text-3xl font-bold text-orange-600">
                        {(stats?.pending_commissions || 0).toLocaleString('fr-FR')} €
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Clock className="text-orange-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-lg border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Déjà Perçu</p>
                      <p className="text-3xl font-bold text-green-600">
                        {(stats?.paid_commissions || 0).toLocaleString('fr-FR')} €
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="text-green-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              <Button 
                variant={activeTab === 'overview' ? 'default' : 'outline'}
                onClick={() => setActiveTab('overview')}
              >
                Vue d'ensemble
              </Button>
              <Button 
                variant={activeTab === 'contracts' ? 'default' : 'outline'}
                onClick={() => setActiveTab('contracts')}
              >
                Mes Contrats ({contracts.length})
              </Button>
              <Button 
                variant={activeTab === 'commissions' ? 'default' : 'outline'}
                onClick={() => setActiveTab('commissions')}
              >
                Mes Commissions ({commissions.length})
              </Button>
              <Button 
                variant={activeTab === 'payments' ? 'default' : 'outline'}
                onClick={() => setActiveTab('payments')}
              >
                Mes Paiements ({payments.length})
              </Button>
              <Button 
                variant={activeTab === 'leads' ? 'default' : 'outline'}
                onClick={() => setActiveTab('leads')}
              >
                <UserPlus size={16} className="mr-1" /> Mes Prospects ({leads.length})
              </Button>
              <Button 
                onClick={() => setShowLeadForm(true)}
                className="ml-auto bg-green-600 hover:bg-green-700"
              >
                <Plus size={16} className="mr-1" /> Nouveau Prospect
              </Button>
            </div>

            {/* Lead Form Modal */}
            {showLeadForm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-lg">
                  <CardHeader className="bg-gradient-to-r from-green-600 to-green-800 text-white rounded-t-lg">
                    <CardTitle className="flex items-center gap-2">
                      <UserPlus size={20} />
                      Soumettre un nouveau prospect
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <form onSubmit={handleSubmitLead} className="space-y-4">
                      <div>
                        <Label>Nom du client *</Label>
                        <Input
                          value={leadFormData.client_name}
                          onChange={(e) => setLeadFormData({...leadFormData, client_name: e.target.value})}
                          placeholder="Jean Dupont"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Téléphone *</Label>
                          <Input
                            value={leadFormData.client_phone}
                            onChange={(e) => setLeadFormData({...leadFormData, client_phone: e.target.value})}
                            placeholder="06 12 34 56 78"
                            required
                          />
                        </div>
                        <div>
                          <Label>Email</Label>
                          <Input
                            type="email"
                            value={leadFormData.client_email}
                            onChange={(e) => setLeadFormData({...leadFormData, client_email: e.target.value})}
                            placeholder="email@exemple.fr"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Type d'assurance recherché *</Label>
                        <Select 
                          value={leadFormData.insurance_type} 
                          onValueChange={(v) => setLeadFormData({...leadFormData, insurance_type: v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner..." />
                          </SelectTrigger>
                          <SelectContent>
                            {INSURANCE_TYPES.map(t => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Détails / Notes</Label>
                        <Textarea
                          value={leadFormData.details}
                          onChange={(e) => setLeadFormData({...leadFormData, details: e.target.value})}
                          placeholder="Informations complémentaires sur le prospect..."
                          rows={3}
                        />
                      </div>
                      <div className="flex gap-3 pt-4">
                        <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                          <Send size={16} className="mr-2" /> Soumettre
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowLeadForm(false)}>
                          Annuler
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Contracts */}
                <Card className="bg-white shadow-lg border-0">
                  <CardHeader>
                    <CardTitle className="text-lg">Derniers Contrats</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {stats?.recent_contracts?.length > 0 ? (
                      <div className="space-y-3">
                        {stats.recent_contracts.map((contract) => (
                          <div key={contract.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                            <div>
                              <p className="font-medium text-slate-900">{contract.client_name}</p>
                              <p className="text-sm text-slate-500">
                                {INSURANCE_LABELS[contract.insurance_type] || contract.insurance_type}
                              </p>
                            </div>
                            <div className="text-right">
                              <span className="text-sm font-medium text-blue-600">
                                {contract.apporteur_commission?.toLocaleString('fr-FR')} €
                              </span>
                              <p className="text-xs text-slate-400">
                                {new Date(contract.start_date).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-4">Aucun contrat récent</p>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Commissions */}
                <Card className="bg-white shadow-lg border-0">
                  <CardHeader>
                    <CardTitle className="text-lg">Dernières Commissions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {stats?.recent_commissions?.length > 0 ? (
                      <div className="space-y-3">
                        {stats.recent_commissions.map((commission) => (
                          <div key={commission.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                            <div>
                              <p className="font-medium text-slate-900">{commission.contract_info?.client_name}</p>
                              <p className="text-sm text-slate-500">
                                {new Date(commission.period_start).toLocaleDateString('fr-FR')} - {new Date(commission.period_end).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                            <div className="text-right">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                commission.status === 'paye' ? 'bg-green-100 text-green-700' :
                                commission.status === 'valide' ? 'bg-blue-100 text-blue-700' :
                                'bg-orange-100 text-orange-700'
                              }`}>
                                {commission.amount?.toLocaleString('fr-FR')} €
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-4">Aucune commission récente</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Contracts Tab */}
            {activeTab === 'contracts' && (
              <Card className="bg-white shadow-lg border-0 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Client</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Type</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Compagnie</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Prime</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Ma Commission</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {contracts.length > 0 ? (
                        contracts.map((contract) => (
                          <tr key={contract.id} className="hover:bg-slate-50">
                            <td className="p-4 font-medium text-slate-900">{contract.client_name}</td>
                            <td className="p-4 text-slate-600">
                              {INSURANCE_LABELS[contract.insurance_type] || contract.insurance_type}
                            </td>
                            <td className="p-4 text-slate-600">{contract.insurance_company}</td>
                            <td className="p-4 text-slate-600">{contract.premium_amount?.toLocaleString('fr-FR')} €</td>
                            <td className="p-4 font-medium text-blue-600">{contract.apporteur_commission?.toLocaleString('fr-FR')} €</td>
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                contract.status === 'actif' ? 'bg-green-100 text-green-700' :
                                contract.status === 'en_attente' ? 'bg-orange-100 text-orange-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {contract.status === 'actif' ? 'Actif' : 
                                 contract.status === 'en_attente' ? 'En attente' : 'Résilié'}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="p-8 text-center text-slate-500">
                            <FileText size={48} className="mx-auto mb-4 text-slate-300" />
                            <p>Aucun contrat</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Commissions Tab */}
            {activeTab === 'commissions' && (
              <Card className="bg-white shadow-lg border-0 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Contrat</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Période</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Montant</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {commissions.length > 0 ? (
                        commissions.map((commission) => (
                          <tr key={commission.id} className="hover:bg-slate-50">
                            <td className="p-4">
                              <p className="font-medium text-slate-900">{commission.contract_info?.client_name}</p>
                              <p className="text-sm text-slate-500">{commission.contract_info?.insurance_type}</p>
                            </td>
                            <td className="p-4 text-slate-600">
                              {new Date(commission.period_start).toLocaleDateString('fr-FR')} - {new Date(commission.period_end).toLocaleDateString('fr-FR')}
                            </td>
                            <td className="p-4 font-medium text-blue-600">{commission.amount?.toLocaleString('fr-FR')} €</td>
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                commission.status === 'paye' ? 'bg-green-100 text-green-700' :
                                commission.status === 'valide' ? 'bg-blue-100 text-blue-700' :
                                'bg-orange-100 text-orange-700'
                              }`}>
                                {commission.status === 'paye' ? 'Payé' : 
                                 commission.status === 'valide' ? 'Validé' : 'En attente'}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="p-8 text-center text-slate-500">
                            <DollarSign size={48} className="mx-auto mb-4 text-slate-300" />
                            <p>Aucune commission</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Payments Tab */}
            {activeTab === 'payments' && (
              <Card className="bg-white shadow-lg border-0 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Date</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Montant</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Mode</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Référence</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {payments.length > 0 ? (
                        payments.map((payment) => (
                          <tr key={payment.id} className="hover:bg-slate-50">
                            <td className="p-4 text-slate-600">
                              {new Date(payment.payment_date).toLocaleDateString('fr-FR')}
                            </td>
                            <td className="p-4 font-bold text-green-600">{payment.total_amount?.toLocaleString('fr-FR')} €</td>
                            <td className="p-4 text-slate-600 capitalize">{payment.payment_method}</td>
                            <td className="p-4 text-slate-500">{payment.reference || '-'}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="p-8 text-center text-slate-500">
                            <DollarSign size={48} className="mx-auto mb-4 text-slate-300" />
                            <p>Aucun paiement reçu</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Leads Tab */}
            {activeTab === 'leads' && (
              <Card className="bg-white shadow-lg border-0 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Client</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Contact</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Type</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Date</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-600">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {leads.length > 0 ? (
                        leads.map((lead) => (
                          <tr key={lead.id} className="hover:bg-slate-50">
                            <td className="p-4">
                              <p className="font-medium text-slate-900">{lead.client_name}</p>
                              {lead.details && <p className="text-sm text-slate-500 truncate max-w-[200px]">{lead.details}</p>}
                            </td>
                            <td className="p-4">
                              <p className="text-sm text-slate-600">{lead.client_phone}</p>
                              {lead.client_email && <p className="text-xs text-slate-400">{lead.client_email}</p>}
                            </td>
                            <td className="p-4 text-slate-600">
                              {INSURANCE_TYPES.find(t => t.value === lead.insurance_type)?.label || lead.insurance_type}
                            </td>
                            <td className="p-4 text-slate-500 text-sm">
                              {new Date(lead.created_at).toLocaleDateString('fr-FR')}
                            </td>
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                lead.status === 'nouveau' ? 'bg-blue-100 text-blue-700' :
                                lead.status === 'contacte' ? 'bg-yellow-100 text-yellow-700' :
                                lead.status === 'converti' ? 'bg-green-100 text-green-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {lead.status === 'nouveau' ? 'Nouveau' :
                                 lead.status === 'contacte' ? 'Contacté' :
                                 lead.status === 'converti' ? 'Converti' : 'Perdu'}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="p-8 text-center text-slate-500">
                            <UserPlus size={48} className="mx-auto mb-4 text-slate-300" />
                            <p>Aucun prospect soumis</p>
                            <Button 
                              onClick={() => setShowLeadForm(true)} 
                              className="mt-4 bg-green-600 hover:bg-green-700"
                            >
                              <Plus size={16} className="mr-1" /> Soumettre un prospect
                            </Button>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
